/* util/stil.js — modulernas egen CSS, utan <style>-element.
 *
 * VARFÖR DEN FINNS
 * ----------------
 * Kart-stacken är kodsplittad: varje FAB, legend och verktyg bär sin egen CSS
 * och skjuter in den först när modulen används. Mönstret var
 *
 *     const s = document.createElement('style');
 *     s.id = STYLE_ID;
 *     s.textContent = CSS;
 *     document.head.appendChild(s);
 *
 * på 123 ställen. Det fungerar — men ett `<style>`-element styrs av CSP:s
 * `style-src-elem`, så länge det mönstret finns kan `style-src` aldrig bli
 * strikt. Mätt 2026-08-25: 23 av injektionerna bygger sin CSS med
 * `${…}`-interpolation och går alltså inte att hasha ens i princip.
 *
 * KONSTRUERBARA STYLESHEETS LIGGER UTANFÖR CSP
 * --------------------------------------------
 * `new CSSStyleSheet()` + `document.adoptedStyleSheets` är CSSOM, och CSSOM är
 * inte CSP-styrt — till skillnad från `<style>`-ELEMENTET. Samma CSS, samma
 * kaskad, men ingen `style-src-elem`-överträdelse. Det är hela poängen: vi
 * behöver inte hasha något, för det finns inget element att hasha.
 *
 * ⚠️ KASKADORDNING. Antagna ark läggs efter dokumentets egna stylesheets i
 * kaskaden — precis där ett `<style>` som `appendChild`:as till `<head>` vid
 * runtime hamnade. Sinsemellan behåller de insättningsordningen. Beteendet är
 * alltså oförändrat; byt inte `[...gamla, ark]` mot något som sorterar om.
 *
 * ⚠️ IDEMPOTENT PÅ `id`. Ett hundratal anropare har kvar sina egna
 * `if (document.getElementById(STYLE_ID)) return;`-vakter. De står kvar med
 * flit: på den moderna vägen hittar de ingenting (det finns inget element att
 * hitta), men i `<style>`-fallbacken längst ned gör de det, och då är de
 * korrekta. Att de inte biter i normalfallet är alltså inte ett problem —
 * dubbelanrop fångas HÄR i stället. Granskat 2026-08-25: 100 av 100 vakter
 * skyddade bara stilinjektionen, inget annat arbete kör om.
 *
 * ⚠️ `replaceSync` KASTAR på `@import`. Ingen av sajtens modul-CSS använder det
 * (verifierat 2026-08-25) — men skriver du en ny som gör det, lägg importen i
 * en riktig CSS-fil i stället.
 */

const antagna = new Map();

// Stödet är universellt i moderna browsers (Chrome 73+, Firefox 101+,
// Safari 16.4+). En browser som saknar det saknar också `style-src-elem`, och
// läser då CSP:ns breda `style-src`-fallback — därför är <style>-vägen nedan
// säker att behålla för dem i stället för att lämna dem helt ostilade.
const HAR_STOD = typeof CSSStyleSheet === 'function'
    && 'replaceSync' in CSSStyleSheet.prototype
    && 'adoptedStyleSheets' in Document.prototype;

/**
 * Lägg till en moduls CSS. Anropa fritt — samma `id` gör bara jobbet en gång.
 *
 * @param {string} id   stabil nyckel, samma som gamla `style.id`
 * @param {string} css  CSS-texten
 */
export function antaStil(id, css) {
    if (!id || antagna.has(id)) return;
    if (HAR_STOD) {
        try {
            const ark = new CSSStyleSheet();
            ark.replaceSync(css);
            document.adoptedStyleSheets = [...document.adoptedStyleSheets, ark];
            antagna.set(id, ark);
            return;
        } catch (e) {
            // Trasig CSS ska inte fälla modulen som anropade oss — ett
            // <style> hade tigit på samma sätt. Faller igenom till fallbacken.
            console.warn('[stil] kunde inte anta ark', id, e);
        }
    }
    if (document.getElementById(id)) { antagna.set(id, null); return; }
    const el = document.createElement('style');
    el.id = id;
    el.textContent = css;
    document.head.appendChild(el);
    antagna.set(id, el);
}

/** Bara för prov: har det här id:t redan lagts till? */
export function harStil(id) {
    return antagna.has(id);
}
